import { Component } from '@theme/component';
import { MediaStartedPlayingEvent, SlideshowSelectEvent, ThemeEvents } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * <r-pdp-player> + <r-pdp-video> — the PDP gallery's video player (WS19).
 *
 * One <r-pdp-player> strip per <slideshow-controls> (play/pause, a seekable
 * timeline, mute toggle) drives the <r-pdp-video> elements inside the gallery's
 * slides. The strip follows the current slide and is `hidden` unless that slide
 * is a native video. Markup: snippets/r-pdp-player.liquid and
 * snippets/r-pdp-video.liquid.
 *
 * The same pair also runs outside a gallery: any `[data-r-player-host]`
 * holding one <r-pdp-video> and one strip (the editorial stage's press-to-play
 * video, blocks/r-story-media.liquid). With no slideshow there is no current
 * slide — the host's one video is always the active one.
 *
 * Why not r-media: r-media is a poster + muted ambient loop with no seek and no
 * sound, deliberately outside `MediaStartedPlayingEvent`. Gallery videos are
 * content — they have sound, a duration and an end — so they need a timeline,
 * an unmute, and to cooperate with every other player on the page. What IS
 * borrowed from r-media is the discipline: sources ship inert (`data-r-src`,
 * `preload="none"`), autoplay is decided by a ladder, `play()`'s promise is the
 * policy oracle, and every visual end-state derives from `data-r-state` alone
 * so a frozen background tab can never strand the UI mid-transition.
 *
 * Playback state (mirrored to `data-r-state` on the player AND the active video):
 *   idle → ready → playing ⇄ paused, plus static / blocked / ended.
 *   static  — the automatic ladder said no (setting off, reduced motion, low
 *             data); zero bytes until the shopper presses play.
 *   blocked — the browser refused muted autoplay (iOS Low Power Mode, policy);
 *             the cue press is the gesture the policy wants.
 *
 * Network: one `src` is chosen at promotion time. Browsers that play HLS
 * natively get the m3u8 (adaptive bitrate); everyone else gets the mp4
 * rendition that fits the connection — 480p on save-data / 2g / 3g, 720p by
 * default, 1080p only for a wide slide on a fast connection.
 *
 * Sound: everything starts muted. Unmute is a gesture, remembered for the
 * gallery session so the next video slide starts with sound. If an unmuted
 * `play()` is refused the player falls back to muted rather than showing a
 * poster, and the sound icon tells the truth.
 *
 * Reveal: the strip is in the DOM whenever the current slide is a video, but
 * it only rises into view (`data-r-revealed`) once that clip is playing —
 * a poster waiting for a press shows the cue alone. It stays up through
 * pause / ended and tucks away when the slide changes.
 *
 * Duration rule: only a clip under `data-r-autoplay-max` seconds (8 by
 * default, a gallery setting) autoplays. Longer videos are content the shopper
 * chooses to watch — they sit `static` with the cue until pressed.
 *
 * Ending (loop off): an untouched gallery advances to the next slide so the
 * media plays through as a slideshow; once the shopper has interacted with the
 * gallery (thumbnail, drag, any player press) the video holds with a replay cue.
 *
 * @typedef {Object} PlayerRefs
 * @property {HTMLButtonElement} toggle
 * @property {HTMLInputElement} timeline
 * @property {HTMLButtonElement} sound
 *
 * @typedef {Object} VideoRefs
 * @property {HTMLVideoElement} video
 * @property {HTMLSourceElement[]} [sources]
 * @property {HTMLButtonElement} [cue]
 */

const SLIDE = 'slideshow-slide';
const RANGE_MAX = 1000;
const AUTO_ADVANCE_DELAY = 600;
const KEYBOARD_STEP_SECONDS = 5;

/** Timeline keys → seconds to seek. Home / End stay native (min / max). */
const SEEK_KEYS = {
  ArrowRight: KEYBOARD_STEP_SECONDS,
  ArrowUp: KEYBOARD_STEP_SECONDS,
  PageUp: KEYBOARD_STEP_SECONDS * 2,
  ArrowLeft: -KEYBOARD_STEP_SECONDS,
  ArrowDown: -KEYBOARD_STEP_SECONDS,
  PageDown: -KEYBOARD_STEP_SECONDS * 2,
};
const WIDE_SLIDE_PX = 900;
const HLS_MIME = 'application/vnd.apple.mpegurl';
const CUE_EVENT = 'r-pdp-video:cue';

/** Player states in which the slide's cue wants a press. */
const PRESSABLE = new Set(['idle', 'static', 'blocked', 'paused', 'ended']);

/** True when the connection is one we should not push HD over. */
function prefersLowData() {
  const connection = /** @type {any} */ (navigator).connection;
  if (connection?.saveData) return true;
  if (['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return true;
  return matchMedia('(prefers-reduced-data: reduce)').matches;
}

/** Native HLS support (Safari, iOS). Chrome/Firefox return ''. */
function playsHls() {
  return document.createElement('video').canPlayType(HLS_MIME) !== '';
}

/**
 * @param {number} seconds
 * @returns {string} m:ss
 */
function formatTime(seconds) {
  const whole = Math.max(0, Math.floor(seconds || 0));
  const minutes = Math.floor(whole / 60);
  const rest = `${whole % 60}`.padStart(2, '0');
  return `${minutes}:${rest}`;
}

/* ↗ .cursor/references/code-notes/r-pdp-player.js.md#§1 */
class RPdpVideo extends Component {
  get video() {
    return this.refs.video;
  }

  get state() {
    return this.dataset.rState ?? 'idle';
  }

  /** @param {string} state */
  set state(state) {
    this.dataset.rState = state;
  }

  get loops() {
    return this.hasAttribute('data-r-loop');
  }

  /** True once a `src` has been chosen and set. */
  get promoted() {
    return Boolean(this.video?.getAttribute('src'));
  }

  /** The chosen source is the HLS manifest (so an mp4 fallback exists). */
  #onHls = false;

  /**
   * `canPlayType` answers "maybe" for HLS on browsers that then fail to play
   * it. On a media error, swap to the best mp4 and let the player retry once.
   * @returns {boolean} true if a fallback was applied
   */
  fallback() {
    const { video } = this;
    if (!video || !this.#onHls) return false;

    const url = this.#chooseSource({ hls: false });
    if (!url) return false;

    this.#onHls = false;
    video.src = url;
    video.load();
    return true;
  }

  /**
   * Choose a source and hand it to the <video>. Idempotent.
   * @param {'metadata' | 'auto'} preload - how eagerly to fetch once promoted
   */
  promote(preload = 'metadata') {
    const { video } = this;
    if (!video) return;

    if (!this.promoted) {
      const url = this.#chooseSource();
      if (!url) return;
      video.src = url;
    }

    // `preload` only ratchets up: a slide promoted one-ahead at `metadata`
    // moves to `auto` when it becomes current, never back.
    if (preload === 'auto' || video.preload === 'none') video.preload = preload;
  }

  /** Declarative `on:click="/cue"` from the slide's cue button. */
  cue() {
    this.dispatchEvent(new CustomEvent(CUE_EVENT, { bubbles: true }));
  }

  /**
   * @param {{ hls?: boolean }} [options] - `hls: false` skips the manifest
   * @returns {string | undefined}
   */
  #chooseSource({ hls: allowHls = true } = {}) {
    // URL sources are art-directed: only those whose `data-r-media` matches
    // now are candidates, so a desktop/mobile pair downloads one file.
    const sources = (this.refs.sources ?? []).filter(
      (source) => !source.dataset.rMedia || matchMedia(source.dataset.rMedia).matches
    );
    const hls = sources.find((source) => source.type.toLowerCase() === HLS_MIME || source.dataset.rFormat === 'm3u8');
    if (allowHls && hls && playsHls()) {
      this.#onHls = true;
      return hls.dataset.rSrc;
    }

    const mp4s = sources
      .filter((source) => source.type === 'video/mp4' && source.dataset.rSrc)
      .map((source) => ({ url: source.dataset.rSrc ?? '', height: Number(source.dataset.rHeight) || 0 }))
      .sort((a, b) => a.height - b.height);

    if (!mp4s.length) return sources[0]?.dataset.rSrc;

    const budget = this.#heightBudget();
    const fitting = mp4s.filter((source) => source.height <= budget);
    return (fitting.length ? fitting[fitting.length - 1] : mp4s[0])?.url;
  }

  #heightBudget() {
    if (prefersLowData()) return 480;

    const connection = /** @type {any} */ (navigator).connection;
    const fast = !connection?.effectiveType || connection.effectiveType === '4g';
    const wide = this.getBoundingClientRect().width * devicePixelRatio > WIDE_SLIDE_PX;
    return fast && wide ? 1080 : 720;
  }
}

/* ↗ .cursor/references/code-notes/r-pdp-player.js.md#§2 */
class RPdpPlayer extends Component {
  requiredRefs = ['toggle', 'timeline', 'sound'];

  /** @type {HTMLElement | null} */
  #gallery = null;

  /** @type {RPdpVideo | null} */
  #active = null;

  /** @type {AbortController | undefined} */
  #listeners;

  /** @type {AbortController | undefined} */
  #videoListeners;

  /** Sound preference for this gallery session. */
  #soundOn = false;

  /** The shopper explicitly paused — blocks every automatic resume. */
  #userPaused = false;

  /** The shopper has touched the gallery — ends auto-advance. */
  #interacted = false;

  /** Dragging the timeline: suppress timeupdate writes to the range. */
  #scrubbing = false;

  /** Guards overlapping play() promises. */
  #playPending = false;

  /** @type {number} */
  #advanceTimer = 0;

  #syncPending = false;

  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#gallery = this.closest('media-gallery, [data-r-player-host]');
    if (!this.#gallery) return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const gallery = this.#gallery;

    gallery.addEventListener(SlideshowSelectEvent.eventName, this.#scheduleSync, { signal });
    gallery.addEventListener(CUE_EVENT, this.#onCue, { signal });

    // Any press inside the gallery — thumbnail, arrow, drag, the strip itself —
    // is the shopper taking over; the slideshow stops advancing on its own.
    gallery.addEventListener('pointerdown', this.#onInteract, { capture: true, passive: true, signal });
    gallery.addEventListener('keydown', this.#onInteract, { capture: true, passive: true, signal });

    // Another player on the page started (the zoom dialog's copy, a video
    // block): yield, so two soundtracks never overlap.
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#onOtherMedia, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.#resync, { signal });
    document.addEventListener('visibilitychange', this.#onVisibility, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    // ↗ .cursor/references/code-notes/r-pdp-player.js.md#§3
    this.#observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const { target } = mutation;
        if (!(target instanceof Element) || target.localName !== SLIDE) continue;
        if (mutation.oldValue === target.getAttribute('aria-hidden')) continue;
        this.#scheduleSync();
        return;
      }
    });
    this.#observer.observe(gallery, {
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['aria-hidden'],
    });

    this.#scheduleSync();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#observer?.disconnect();
    this.#detach();
    clearTimeout(this.#advanceTimer);
    super.disconnectedCallback();
  }

  /* ------------------------------------------------------------------ */
  /* Declarative handlers (on:click / on:input / on:pointerdown / on:change) */

  /** Play / pause / replay. */
  toggle() {
    const active = this.#active;
    if (!active) return;

    if (active.state === 'playing') {
      this.#userPaused = true;
      this.#pause(active);
      return;
    }

    this.#userPaused = false;
    if (active.state === 'ended') active.video.currentTime = 0;
    this.#play(active, { userInitiated: true });
  }

  toggleSound() {
    this.#soundOn = !this.#soundOn;
    this.#renderSound();

    const active = this.#active;
    if (!active) return;

    active.video.muted = !this.#soundOn;

    // Turning sound on from a poster state is also a press to play.
    if (this.#soundOn && PRESSABLE.has(active.state) && active.state !== 'paused') {
      this.#userPaused = false;
      if (active.state === 'ended') active.video.currentTime = 0;
      this.#play(active, { userInitiated: true });
    }
  }

  /** @param {Event} event */
  seek(event) {
    const active = this.#active;
    const { timeline } = this.refs;
    if (!active || !(event.target instanceof HTMLInputElement)) return;

    const duration = this.#duration(active);
    if (!duration) return;

    active.promote('auto');
    active.video.currentTime = (Number(timeline.value) / RANGE_MAX) * duration;
    this.#renderProgress(active);
  }

  /**
   * Arrow / Page keys seek by whole seconds. This used to ride the range's
   * `step`, which also quantised every programmatic value: the thumb could
   * only rest on multiples of the step (136 of 1000 on a 37 s clip, 625 on an
   * 8 s one), so it sat visibly off the filled track and never reached the end.
   * The range now keeps step 1 and the keys are handled here.
   * @param {KeyboardEvent} event
   */
  seekKey(event) {
    const delta = SEEK_KEYS[/** @type {keyof typeof SEEK_KEYS} */ (event.key)];
    const active = this.#active;
    if (!delta || !active) return;

    const duration = this.#duration(active);
    if (!duration) return;

    event.preventDefault();
    active.promote('auto');
    active.video.currentTime = Math.min(duration, Math.max(0, active.video.currentTime + delta));
    this.#renderProgress(active);
  }

  scrubStart() {
    this.#scrubbing = true;
    this.setAttribute('data-r-scrubbing', '');
  }

  scrubEnd() {
    this.#scrubbing = false;
    this.removeAttribute('data-r-scrubbing');
  }

  /* ------------------------------------------------------------------ */
  /* Binding */

  /* ↗ .cursor/references/code-notes/r-pdp-player.js.md#§4 */
  #scheduleSync = () => {
    if (this.#syncPending) return;
    this.#syncPending = true;
    queueMicrotask(() => {
      this.#syncPending = false;
      if (this.isConnected) this.#sync();
    });
  };

  #resync = () => {
    this.#scheduleSync();
  };

  /**
   * The slide the slideshow is currently showing. `slideshow.current` is
   * written synchronously by every select path; `aria-hidden` is the fallback
   * for a slideshow that has not set up yet. (The attribute alone is not
   * enough: it is maintained by an IntersectionObserver, which never fires in
   * a background tab, so a page opened behind another can report several
   * slides visible at once.)
   * @returns {Element | null}
   */
  #currentSlide() {
    const slideshow = /** @type {any} */ (this.#gallery?.querySelector('slideshow-component'));
    const slides = slideshow?.slides;
    const fromIndex = Array.isArray(slides) ? slides[slideshow.current] : null;
    if (fromIndex instanceof Element) return fromIndex;
    return this.#gallery?.querySelector(`${SLIDE}[aria-hidden="false"]`) ?? null;
  }

  /** The <r-pdp-video> inside the current slide — or, on a standalone host,
   *  the host's only video. */
  #currentVideo() {
    const scope = this.#gallery?.localName === 'media-gallery' ? this.#currentSlide() : this.#gallery;
    const video = scope?.querySelector('r-pdp-video');
    return video instanceof RPdpVideo ? video : null;
  }

  #sync() {
    const next = this.#currentVideo();

    if (next !== this.#active) {
      this.#detach();
      if (next) this.#attach(next);
    }

    this.hidden = !this.#active;
    this.#prefetchNeighbours();
    this.#syncPlayback();
  }

  /** @param {RPdpVideo} target */
  #attach(target) {
    this.#active = target;
    this.#videoListeners = new AbortController();
    const { signal } = this.#videoListeners;
    const { video } = target;

    video.muted = !this.#soundOn;

    video.addEventListener('durationchange', () => this.#renderProgress(target), { signal });
    video.addEventListener('ended', () => this.#onEnded(target), { signal });

    // ↗ .cursor/references/code-notes/r-pdp-player.js.md#§5
    video.addEventListener('waiting', () => this.setAttribute('data-r-buffering', ''), { signal });
    video.addEventListener(
      'stalled',
      () => {
        if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) this.setAttribute('data-r-buffering', '');
      },
      { signal }
    );
    video.addEventListener(
      'timeupdate',
      () => {
        if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) this.removeAttribute('data-r-buffering');
        this.#renderProgress(target);
      },
      { signal }
    );
    video.addEventListener('playing', () => this.removeAttribute('data-r-buffering'), { signal });

    // A "maybe" that was not to be: drop from HLS to mp4 once and retry.
    video.addEventListener(
      'error',
      () => {
        this.removeAttribute('data-r-buffering');
        if (target.fallback() && target === this.#active && !this.#userPaused) {
          this.#play(target, { userInitiated: false });
        }
      },
      { signal }
    );
    // The media element is the source of truth for playing/paused — a pause
    // from the OS media keys or a Picture-in-Picture window still lands here.
    video.addEventListener('play', () => this.#setState(target, 'playing'), { signal });
    video.addEventListener(
      'pause',
      () => {
        if (target.state === 'playing') this.#setState(target, 'paused');
      },
      { signal }
    );

    // ↗ .cursor/references/code-notes/r-pdp-player.js.md#§6
    if ((target.state === 'idle' || target.state === 'ready') && !this.#shouldAutoplay(target)) {
      this.#setState(target, 'static');
    }

    // ↗ .cursor/references/code-notes/r-pdp-player.js.md#§7
    this.dataset.rState = target.state;
    this.toggleAttribute('data-r-revealed', target.state === 'playing');

    this.#renderProgress(target);
    this.#renderSound();
  }

  #detach() {
    const previous = this.#active;
    this.#videoListeners?.abort();
    this.#videoListeners = undefined;
    this.#active = null;
    clearTimeout(this.#advanceTimer);
    this.removeAttribute('data-r-buffering');
    this.removeAttribute('data-r-revealed');
    this.scrubEnd();

    if (!previous) return;

    // Leaving a slide: stop, rewind, and leave it ready to restart cleanly.
    previous.video.pause();
    if (previous.promoted) {
      previous.video.currentTime = 0;
      // A clip that will not autoplay on return must show its cue, not a
      // bare `ready` frame — the shopper pressed play once; they press again.
      this.#setState(previous, this.#shouldAutoplay(previous) ? 'ready' : 'static');
    }

    this.#userPaused = false;
  }

  /** Promote the slides either side of the current one so the next arrow
   *  click starts instantly; `metadata` keeps the cost to a header fetch. */
  #prefetchNeighbours() {
    if (!this.#shouldAutoplay() && !this.#active) return;

    const slide = this.#currentSlide();
    if (!slide) return;

    for (const sibling of [slide.nextElementSibling, slide.previousElementSibling]) {
      const video = sibling?.querySelector('r-pdp-video');
      if (!(video instanceof RPdpVideo) || video.state === 'static') continue;
      // Only a clip that will actually autoplay is worth a header fetch.
      if (this.#shouldAutoplay(video)) video.promote('metadata');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Playback */

  /**
   * Automatic-playback eligibility. User activation is handled separately.
   * With a target, the duration rule applies too: only a short clip (under
   * `data-r-autoplay-max` seconds, 8 by default) starts on its own — anything
   * longer is content the shopper chooses to watch, so it waits for a press.
   * @param {RPdpVideo | null} [target]
   */
  #shouldAutoplay(target = null) {
    if (!this.hasAttribute('data-r-autoplay')) return false;
    if (prefersReducedMotion()) return false;
    if (prefersLowData()) return false;
    if (target && !this.#shortEnough(target)) return false;
    return true;
  }

  /**
   * Duration gate. An unknown duration (no Liquid value, no metadata yet)
   * does not qualify — the rule is "under N seconds", not "not known to be
   * over". A limit of 0 disables the gate.
   * @param {RPdpVideo} target
   */
  #shortEnough(target) {
    const max = Number(this.dataset.rAutoplayMax);
    if (!Number.isFinite(max) || max <= 0) return true;

    const duration = this.#duration(target);
    return duration > 0 && duration < max;
  }

  #syncPlayback() {
    const active = this.#active;
    if (!active || document.hidden || this.#userPaused) return;
    if (active.state === 'playing' || active.state === 'ended') return;

    // `static` and `blocked` wait for a press; everything else may auto-start.
    if (active.state === 'static' || active.state === 'blocked') return;
    if (!this.#shouldAutoplay(active)) return;

    this.#play(active, { userInitiated: false });
  }

  /**
   * @param {RPdpVideo} target
   * @param {{ userInitiated: boolean }} options
   */
  async #play(target, { userInitiated }) {
    if (this.#playPending || target !== this.#active) return;

    const { video } = target;
    target.promote('auto');
    if (!target.promoted) return;

    if (target.state !== 'playing') this.#setState(target, 'ready');
    this.#playPending = true;
    video.muted = !this.#soundOn;

    try {
      await video.play();
    } catch (error) {
      const name = error instanceof DOMException ? error.name : '';

      if (name === 'NotAllowedError' && !video.muted) {
        // Unmuted autoplay refused: fall back to muted rather than to a poster,
        // and let the sound icon say so.
        video.muted = true;
        this.#soundOn = false;
        this.#renderSound();
        try {
          await video.play();
        } catch {
          this.#setState(target, userInitiated ? 'paused' : 'blocked');
        }
      } else if (name === 'NotAllowedError') {
        // iOS Low Power Mode, browser policy. The cue stays; that press is
        // exactly the gesture the policy wants.
        this.#setState(target, userInitiated ? 'paused' : 'blocked');
      } else if (name === 'NotSupportedError' && target.fallback()) {
        // The chosen rendition will not decode here: retry once on mp4.
        this.#playPending = false;
        return this.#play(target, { userInitiated });
      } else {
        // ↗ .cursor/references/code-notes/r-pdp-player.js.md#§8
        if (target.state !== 'playing') this.#setState(target, 'paused');
      }
    } finally {
      this.#playPending = false;
    }

    if (target === this.#active && target.state === 'playing' && !video.muted) {
      this.dispatchEvent(new MediaStartedPlayingEvent(this));
    }
  }

  /** @param {RPdpVideo} target */
  #pause(target) {
    target.video.pause();
    if (target.state === 'playing') this.#setState(target, 'paused');
  }

  /** @param {RPdpVideo} target */
  #onEnded(target) {
    this.#setState(target, 'ended');
    this.#renderProgress(target);

    if (target.loops || this.#interacted || !this.#shouldAutoplay()) return;

    clearTimeout(this.#advanceTimer);
    this.#advanceTimer = window.setTimeout(() => {
      if (target !== this.#active || this.#interacted) return;
      const slideshow = /** @type {any} */ (this.#gallery?.querySelector('slideshow-component'));
      slideshow?.next?.();
    }, AUTO_ADVANCE_DELAY);
  }

  /**
   * @param {RPdpVideo} target
   * @param {string} state
   */
  #setState(target, state) {
    target.state = state;
    if (target !== this.#active) return;

    this.dataset.rState = state;
    // ↗ .cursor/references/code-notes/r-pdp-player.js.md#§9
    if (state === 'playing') this.setAttribute('data-r-revealed', '');
  }

  /* ------------------------------------------------------------------ */
  /* Rendering */

  /** @param {RPdpVideo} target */
  #duration(target) {
    const { duration } = target.video;
    if (Number.isFinite(duration) && duration > 0) return duration;
    return Number(target.dataset.rDuration) || 0;
  }

  /** @param {RPdpVideo} target */
  #renderProgress(target) {
    if (target !== this.#active) return;

    const { timeline } = this.refs;
    const duration = this.#duration(target);
    const current = target.video.currentTime || 0;
    const fraction = duration ? Math.min(1, current / duration) : 0;

    this.style.setProperty('--r-pdp-player-progress', fraction.toFixed(4));

    if (!this.#scrubbing) timeline.value = `${Math.round(fraction * RANGE_MAX)}`;

    const template = this.dataset.rLabelPosition || '%c / %t';
    timeline.setAttribute(
      'aria-valuetext',
      template.replace('%c', formatTime(current)).replace('%t', formatTime(duration))
    );
  }

  #renderSound() {
    this.dataset.rSound = this.#soundOn ? 'on' : 'off';
    this.refs.sound.setAttribute('aria-pressed', `${this.#soundOn}`);
  }

  /* ------------------------------------------------------------------ */
  /* Events */

  #onCue = () => {
    const active = this.#active;
    if (!active) return;

    this.#userPaused = false;
    if (active.state === 'ended') active.video.currentTime = 0;
    this.#play(active, { userInitiated: true });
  };

  #onInteract = () => {
    this.#interacted = true;
    clearTimeout(this.#advanceTimer);
  };

  /** @param {Event} event */
  #onOtherMedia = (event) => {
    const resource = /** @type {MediaStartedPlayingEvent} */ (event).detail?.resource;
    // Our own dispatch, or the twin strip (desktop/mobile) driving the same video.
    if (!resource || resource instanceof RPdpPlayer) return;

    const active = this.#active;
    if (active?.state === 'playing') this.#pause(active);
  };

  #onVisibility = () => {
    if (document.hidden) {
      const active = this.#active;
      if (active?.state === 'playing') this.#pause(active);
    } else {
      this.#scheduleSync();
    }
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#scheduleSync();
  };
}

if (!customElements.get('r-pdp-video')) {
  customElements.define('r-pdp-video', RPdpVideo);
}

if (!customElements.get('r-pdp-player')) {
  customElements.define('r-pdp-player', RPdpPlayer);
}
